import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Sidebar from "./Sidebarlaunchpad";
import { useTenant } from "../Context/TenantContext";
import EliteMarkdownViewer from "./EliteMarkdownViewer";
import ThemeToggle from "./ThemeToggle";
import LockedPopup from "./LockedPopup";
import { useSubscription } from "../Context/SubscriptionContext";
import ChatBot from "../components/SkillGenBot/ChatBot"; 
import API_BASE_URL from "../Config/config";
import { useTheme } from "../contexts/ThemeContext";
import { 
  BookOpen, 
  ChevronDown, 
  Search,
  Clock,
  ThumbsUp,
  ThumbsDown,
  ArrowLeft,
  ArrowRight,
  Rocket
} from "lucide-react";

const LaunchpadReader = () => {
  const [filePath, setFilePath] = useState("");
  const { tenant } = useTenant();
  const { subscription, showPremiumPopup } = useSubscription();
  const [visited, setVisited] = useState([]);
  const contentRef = useRef(null);
  const { theme } = useTheme();
  const isDark = theme === "dark";

  const isPaid = subscription.isPaid;

  // Check if this is a tenant student (college flow)
  const isTenantMode = !!tenant;
  const navigate = useNavigate();

  const [isPremium, setIsPremium] = useState(isPaid || isTenantMode);

  useEffect(() => {
    setIsPremium(isPaid || isTenantMode);
  }, [isPaid, isTenantMode]);

  const handleSelect = (path, isLocked = false, index = 0) => {
    if (isLocked) {
      showPremiumPopup();
      return;
    }
    setFilePath(path);
    setDocIndex(index);
  };

  const [docIndex, setDocIndex] = useState(0);

  return (
    <div className={`${isDark ? "bg-[#0B0F1A]" : "bg-[#F8FAFC]"} min-h-screen flex flex-col font-sans selection:bg-primary/30`}>
      {/* 🧭 Top Navigation Bar */}
      <header className={`h-16 flex items-center justify-between px-6 sticky top-0 z-[70] border-b backdrop-blur-md ${
        isDark ? "bg-[#0B0F1A]/80 border-white/5" : "bg-white/80 border-gray-200"
      }`}>
        <div className="flex items-center gap-4">
          <button 
            onClick={() => navigate('/dashboard')}
            className={`p-2 rounded-lg transition-all ${
              isDark ? "hover:bg-white/10 text-gray-400" : "hover:bg-gray-100 text-gray-600"
            }`}
            title="Go Back"
          >
            <ArrowLeft size={20} />
          </button>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center shadow-lg shadow-primary/20">
              <BookOpen className="text-white" size={18} />
            </div>
            <span className={`text-lg font-bold tracking-tight ${isDark ? "text-white" : "text-slate-900"}`}>
              The Skill Genie
            </span>
          </div>
          <div className={`h-6 w-px mx-2 ${isDark ? "bg-white/10" : "bg-gray-200"}`} />
          <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-sm font-medium transition-colors ${
            isDark ? "bg-white/5 border-white/10 text-gray-300" : "bg-gray-50 border-gray-200 text-gray-600"
          }`}>
            <span>Launchpad</span>
            <ChevronDown size={14} className="opacity-50" />
          </div>
        </div>

        <div className="flex-1 max-w-2xl mx-12 hidden md:block">
          <div className={`relative group flex items-center ${isDark ? "text-gray-400" : "text-gray-500"}`}>
            <Search className="absolute left-3 group-focus-within:text-primary transition-colors" size={18} />
            <input 
              type="text" 
              placeholder="Search documentation..." 
              className={`w-full bg-transparent border rounded-xl pl-10 pr-16 py-2 text-sm outline-none transition-all ${
                isDark 
                ? "border-white/10 focus:border-primary/50 focus:bg-white/5" 
                : "border-gray-200 focus:border-primary/50 focus:bg-gray-50"
              }`}
            />
            <div className={`absolute right-3 px-1.5 py-0.5 rounded border text-[10px] font-mono ${
              isDark ? "border-white/10 text-gray-500" : "border-gray-200 text-gray-400"
            }`}>
              Ctrl K
            </div>
          </div>
        </div>

        <div className="flex items-center gap-4">
          <ThemeToggle />
        </div>
      </header>

      <div className="flex flex-1 overflow-hidden relative">
        {/* 📚 Sidebar */}
        <aside className={`
          w-72 border-r transition-all duration-300 ease-in-out
          sticky top-0 h-[calc(100vh-64px)] overflow-y-auto
          ${isDark ? "bg-[#0B0F1A] border-white/5" : "bg-white border-gray-100"}
        `}>
          <div className="h-full flex flex-col">
            <div className="p-4 uppercase text-[10px] font-bold tracking-[0.2em] text-gray-500">
              Launchpad Library
            </div>
            <div className="flex-1 overflow-y-auto">
              <Sidebar 
                onSelect={handleSelect} 
                selectedPath={filePath} 
                isPremium={isPremium}
              />
            </div>
          </div>
        </aside>

        <main ref={contentRef} className="flex-1 overflow-y-auto relative bg-transparent scroll-smooth">
          {filePath ? (
            <div className="max-w-6xl mx-auto px-6 py-8 relative z-10">
              {/* Top Meta info */}
              <div className="flex items-center justify-between mb-8 text-[13px]">
                <div className="flex items-center gap-3 text-gray-500">
                  <span className="opacity-60">You're reading</span>
                  <div className="flex items-center gap-2">
                    <span className={`font-semibold ${isDark ? "text-gray-300" : "text-slate-700"}`}>
                      Launchpad Guide
                    </span>
                    <span className="opacity-30">•</span>
                    <div className="flex items-center gap-1.5">
                      <div className="w-4 h-4 rounded-full border-2 border-primary/30 flex items-center justify-center">
                        <div className="w-1.5 h-1.5 rounded-full bg-primary" />
                      </div>
                      <span>45% complete</span>
                    </div>
                    <span className="opacity-30">•</span>
                    <div className="flex items-center gap-1.5">
                      <Clock className="w-3.5 h-3.5" />
                      <span>10 min read</span>
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center gap-6">
                  <div className="flex items-center gap-3">
                    <span className="text-gray-500">Was this helpful?</span>
                    <div className="flex items-center gap-2">
                      <button 
                        onClick={() => {
                          alert("Thank you for your feedback!");
                        }}
                        className={`p-1.5 rounded-lg border transition-all ${
                        isDark ? "border-white/10 hover:bg-white/5 text-gray-400" : "border-gray-200 hover:bg-gray-50 text-gray-500"
                      }`}>
                        <ThumbsUp className="w-4 h-4" />
                      </button>
                      <button 
                        onClick={() => {
                          alert("Thank you for your feedback! We will try to improve.");
                        }}
                        className={`p-1.5 rounded-lg border transition-all ${
                        isDark ? "border-white/10 hover:bg-white/5 text-gray-400" : "border-gray-200 hover:bg-gray-50 text-gray-500"
                      }`}>
                        <ThumbsDown className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <EliteMarkdownViewer filePath={filePath} docIndex={docIndex} />
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-full text-center max-w-lg mx-auto p-10">
              <div className="w-24 h-24 rounded-3xl bg-primary/10 flex items-center justify-center mb-8 border border-primary/20 shadow-inner">
                <span className="text-5xl animate-bounce-slow">🚀</span>
              </div>
              <h3 className={`text-2xl font-bold mb-4 ${isDark ? "text-white" : "text-slate-900"}`}>Launchpad Library</h3>
              <p className="text-slate-500 text-lg leading-relaxed">
                Select a topic from the sidebar to start learning. Master industry-standard engineering patterns.
              </p>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default LaunchpadReader;



